import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, 
  Globe, 
  Clock, 
  Phone, 
  Mail, 
  Calendar, 
  Save,
  X,
  Loader2,
  Camera,
  ShieldCheck,
  CheckCircle2,
  Medal,
  FileText,
  Timer,
  Check,
  AlarmMinus,
  Activity
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Autocomplete from '@mui/material/Autocomplete';
import { TextField } from "@mui/material";

import SnackbarMessage from "../../components/Snackbar";
import InputField from "../../components/FormFilelds/InputField";
import DatePickerInput from "../../components/FormFilelds/Datepicker";
import SelectInput from "../../components/FormFilelds/selectinput";
import PhoneNumberInput from "../../components/FormFilelds/PhoneNumberInput";
import ProviderUnavailabilityModal from "../../components/Modals/ProviderUnavailabilityModal";
import ProviderAvailabilityModal from "../../components/Modals/ProviderAvailabilityModal";

import {
  MastersData,
  TimezonesData,
  UserData,
  ProviderInfo,
} from "../../components/interface/providerinterface";
import {
  fetchMasters,
  fetchTimezones,
  fetchUserInfo,
  updateUser,
  fetchProviderInfo,
  updateProvider,
  deleteProviderUnavailability,
  fetchProviderUnavailabilities,
  uploadProfileImage,
} from "../../services/providerService";

interface Timezone {
  id: number;
  country_code: string;
  zone_name: string;
}

const Accounts: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(null);
  const [unavailabilitiesList, setUnavailabilitiesList] = useState<any[]>([]);
  const [mastersData, setMastersData] = useState<MastersData | null>(null);
  const [timezonesData, setTimezonesData] = useState<TimezonesData | null>(null);
  
  // Form States
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState<Date | null>(null);
  const [gender, setGender] = useState("");
  const [blood_group, setBlood_group] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setmobile] = useState("");
  const [isdCode, setIsdCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<{ value: string } | null>(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState<{ zone_name: string; country_code: string } | null>(null);
  
  const [stateOptions, setStateOptions] = useState<{ label: string, value: string }[]>([]);
  const [timeZones, setTimeZones] = useState<Timezone[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" as any });

  const [activeDay, setActiveDay] = useState<string>("Monday");
  const [showUnavailability, setShowUnavailability] = useState(false);
  const [showAvailability, setShowAvailability] = useState(false);
  const [newSlotTime, setNewSlotTime] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userData?.data?.id) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setSnackbar({ open: true, message: "Please select a valid image file.", severity: "error" });
      return;
    }
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: "Image must be smaller than 5MB.", severity: "error" });
      return;
    }

    try {
      setImageUploading(true);
      const newImageUrl = await uploadProfileImage(userData.data.id, file);
      // Refresh user data to get the updated profile image URL
      const updated = await fetchUserInfo();
      setUserData(updated);
      setSnackbar({ open: true, message: "Profile photo updated successfully!", severity: "success" });
    } catch (err) {
      console.error("Image upload error:", err);
      setSnackbar({ open: true, message: "Failed to upload profile image. Please try again.", severity: "error" });
    } finally {
      setImageUploading(false);
      // Reset file input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDeleteUnavailability = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this unavailability block?")) {
      return;
    }
    try {
      setLoading(true);
      await deleteProviderUnavailability(id);
      setSnackbar({ open: true, message: "Unavailability block deleted successfully!", severity: "success" });
      
      // Refresh provider info and unavailability list
      if (userData?.data?.id) {
        const [provider, unavailData] = await Promise.all([
          fetchProviderInfo(userData.data.id),
          fetchProviderUnavailabilities()
        ]);
        setProviderInfo(provider);
        if (unavailData?.data?.provider_unavailabilities) {
          setUnavailabilitiesList(unavailData.data.provider_unavailabilities);
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete unavailability block", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveDay = async () => {
    if (!providerInfo) return;
    const currentDayAvailability = providerInfo.data.availabilities.find(a => a.text === activeDay);
    const updatedDayStatus = !currentDayAvailability?.day;
    
    const newAvailabilities = providerInfo.data.availabilities.map(day => 
      day.text === activeDay ? { ...day, day: updatedDayStatus } : day
    );

    try {
      setLoading(true);
      await updateProvider(providerInfo.data.id, {
        provider_id: String(providerInfo.data.id),
        availabilities: newAvailabilities
      });
      setProviderInfo({ ...providerInfo, data: { ...providerInfo.data, availabilities: newAvailabilities } });
      setSnackbar({ open: true, message: `Marked ${activeDay} as ${updatedDayStatus ? 'Operational' : 'Off-Duty'}`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update availability", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async () => {
    if (!providerInfo || !newSlotTime) return;
    const currentDayAvailability = providerInfo.data.availabilities.find(a => a.text === activeDay);
    if (!currentDayAvailability) return;
    
    if (currentDayAvailability.timing.slot.some(s => s.time === newSlotTime)) {
       setSnackbar({ open: true, message: "Time slot already exists", severity: "error" });
       return;
    }

    const newAvailabilities = providerInfo.data.availabilities.map(day => {
      if (day.text === activeDay) {
        return {
          ...day,
          timing: {
            ...day.timing,
            slot: [...day.timing.slot, { time: newSlotTime, status: "available" }].sort((a, b) => a.time.localeCompare(b.time))
          }
        };
      }
      return day;
    });

    try {
      setLoading(true);
      await updateProvider(providerInfo.data.id, {
        provider_id: String(providerInfo.data.id),
        availabilities: newAvailabilities
      });
      setProviderInfo({ ...providerInfo, data: { ...providerInfo.data, availabilities: newAvailabilities } });
      setNewSlotTime("");
      setSnackbar({ open: true, message: "New time slot added successfully", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to add time slot", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const [user, masters, tz] = await Promise.all([
          fetchUserInfo(),
          fetchMasters(),
          fetchTimezones()
        ]);
        setUserData(user);
        setMastersData(masters);
        setTimezonesData(tz);

        // After user info is fetched, fetch provider info
        if (user?.data?.id) {
          const [provider, unavailData] = await Promise.all([
            fetchProviderInfo(user.data.id),
            fetchProviderUnavailabilities()
          ]);
          setProviderInfo(provider);
          if (unavailData?.data?.provider_unavailabilities) {
            setUnavailabilitiesList(unavailData.data.provider_unavailabilities);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (userData?.data && mastersData?.data) {
      const u = userData.data;
      setFirstName(u.first_name || "");
      setLastName(u.last_name || "");
      setDob(u.dob ? new Date(u.dob) : null);
      setBlood_group(u.blood_group || "");
      setGender(u.gender || "");
      setAddress1(u.address?.address1 || "");
      setAddress2(u.address?.address2 || "");
      setCity(u.address?.city || "");
      setZipcode(u.address?.zipcode || "");
      setState(u.address?.state || "");
      setEmail(u.email || "");
      setmobile(u.mobile || "");
      setIsdCode(u.isd_code || "");
      
      if (u.address?.country) {
        setSelectedCountry({ value: u.address.country });
        const selectedCountryData = mastersData.data.masters.find(m => m.name === u.address?.country);
        if (selectedCountryData) handleCountryChange(u.address.country, selectedCountryData);
      }
      
      if (u.timezone?.zone_name) {
        setSelectedTimeZone({
          zone_name: u.timezone.zone_name,
          country_code: u.timezone.country_code || "",
        });
      }
    }
  }, [userData, mastersData]);

  const handleCountryChange = (countryName: string, countryData?: any) => {
    setSelectedCountry({ value: countryName });
    const master = countryData || mastersData?.data.masters.find(m => m.name === countryName);
    
    if (master) {
      if (timezonesData?.data?.timezones) {
        const zones = Array.isArray(timezonesData.data.timezones[0]) ? timezonesData.data.timezones[0] : [];
        setTimeZones(zones.filter(z => z.country_code === master.slug));
      }
      if (master.attributes?.states) {
        setStateOptions(master.attributes.states.map((s: string) => ({ label: s, value: s })));
      }
    }
  };

  const handleSave = async () => {
    if (!userData?.data?.id) return;
    
    const timezoneId = selectedTimeZone
      ? timezonesData?.data.timezones.flat().find(z => z.zone_name === selectedTimeZone.zone_name)?.id
      : userData.data.timezone?.id;

    const payload = {
      ...userData.data,
      first_name: firstName,
      last_name: lastName,
      email: email,
      mobile: mobile,
      gender: gender,
      dob: dob ? dob.toISOString().split("T")[0] : null,
      blood_group: blood_group,
      timezone: {
        id: timezoneId,
        zone_name: selectedTimeZone?.zone_name || userData.data.timezone!.zone_name,
        country_code: selectedTimeZone?.country_code || userData.data.timezone!.country_code,
      },
      address: {
        city: city,
        state: state,
        country: selectedCountry?.value,
        zipcode: zipcode,
        address1: address1,
        address2: address2,
      },
      timezone_id: timezoneId,
    };

    try {
      setLoading(true);
      await updateUser(userData.data.id, payload);
      setSnackbar({ open: true, message: "Profile updated successfully!", severity: "success" });
      const updated = await fetchUserInfo();
      setUserData(updated);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to update profile", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userData) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
        <p className="mt-4 text-slate-500 font-medium tracking-widest uppercase text-[10px]">Loading clinical profile...</p>
      </div>
    );
  }

  const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(v => ({ label: v, value: v }));
  const genderOptions = ["male", "female", "other"].map(v => ({ label: v.charAt(0).toUpperCase() + v.slice(1), value: v }));
  const countryOptions = mastersData?.data.masters.map(m => ({ label: m.name, value: m.name })) || [];
  const timeZoneOptions = timeZones.map(z => ({ label: z.zone_name, value: z.zone_name }));

  const currentDayAvailability = providerInfo?.data.availabilities.find(a => a.text === activeDay);

  return (
    <div className="max-w-6xl mx-auto py-8 px-4 font-primary">
      <header className="mb-8 md:mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Provider Profile</h1>
          <p className="text-slate-500 font-medium italic">Professional credentials and clinical availability.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <button 
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 rounded-2xl border border-slate-200 text-slate-500 font-bold hover:bg-slate-50 transition-all flex justify-center items-center gap-2 flex-1"
          >
            <X size={18} /> Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={loading}
            className="px-8 py-2.5 rounded-2xl bg-primary text-white font-black shadow-lg shadow-primary/25 hover:scale-[1.02] active:scale-[0.98] transition-all flex justify-center items-center gap-2 disabled:opacity-50 flex-1"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} 
            Save Profile
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column: Avatar and Quick Stats */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white rounded-[2.5rem] p-8 border border-slate-100 shadow-sm text-center relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-br from-primary to-primary-dark opacity-5 group-hover:opacity-10 transition-opacity" />
            
            <div className="relative mt-8 mb-6 inline-block">
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden"
                style={{
                  background: gender === "female"
                    ? "linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)"
                    : gender === "male"
                    ? "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)"
                    : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)"
                }}
              >
                {userData?.data?.profile_image ? (
                  <img src={userData.data.profile_image} alt="Profile" className="w-full h-full object-cover" />
                ) : gender === "female" ? (
                  /* Female avatar SVG */
                  <svg viewBox="0 0 128 128" className="w-24 h-24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Body / dress */}
                    <ellipse cx="64" cy="108" rx="30" ry="18" fill="#f48fb1" />
                    <path d="M38 95 Q64 115 90 95 L84 75 Q64 85 44 75 Z" fill="#f48fb1" />
                    {/* Neck */}
                    <rect x="58" y="62" width="12" height="14" rx="6" fill="#FFCC99" />
                    {/* Head */}
                    <ellipse cx="64" cy="50" rx="22" ry="24" fill="#FFCC99" />
                    {/* Hair */}
                    <ellipse cx="64" cy="30" rx="22" ry="12" fill="#b71c1c" />
                    <ellipse cx="42" cy="50" rx="5" ry="14" fill="#b71c1c" />
                    <ellipse cx="86" cy="50" rx="5" ry="14" fill="#b71c1c" />
                    {/* Eyes */}
                    <ellipse cx="56" cy="50" rx="3" ry="3.5" fill="#4e342e" />
                    <ellipse cx="72" cy="50" rx="3" ry="3.5" fill="#4e342e" />
                    {/* Smile */}
                    <path d="M57 60 Q64 66 71 60" stroke="#c2185b" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  </svg>
                ) : gender === "male" ? (
                  /* Male avatar SVG */
                  <svg viewBox="0 0 128 128" className="w-24 h-24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Body / shirt */}
                    <path d="M34 128 Q34 88 64 88 Q94 88 94 128 Z" fill="#1565c0" />
                    <path d="M44 80 L34 100 Q64 110 94 100 L84 80 Q64 92 44 80 Z" fill="#1976d2" />
                    {/* Neck */}
                    <rect x="58" y="62" width="12" height="14" rx="6" fill="#FFCC99" />
                    {/* Head */}
                    <ellipse cx="64" cy="50" rx="22" ry="24" fill="#FFCC99" />
                    {/* Hair */}
                    <path d="M42 38 Q44 22 64 20 Q84 22 86 38 Q80 28 64 28 Q48 28 42 38 Z" fill="#37474f" />
                    {/* Eyes */}
                    <ellipse cx="56" cy="50" rx="3" ry="3.5" fill="#37474f" />
                    <ellipse cx="72" cy="50" rx="3" ry="3.5" fill="#37474f" />
                    {/* Smile */}
                    <path d="M57 60 Q64 66 71 60" stroke="#795548" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  </svg>
                ) : (
                  /* Neutral / Other avatar SVG */
                  <svg viewBox="0 0 128 128" className="w-24 h-24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Body */}
                    <path d="M34 128 Q34 88 64 88 Q94 88 94 128 Z" fill="#90a4ae" />
                    <path d="M44 80 L34 100 Q64 110 94 100 L84 80 Q64 92 44 80 Z" fill="#b0bec5" />
                    {/* Neck */}
                    <rect x="58" y="62" width="12" height="14" rx="6" fill="#FFCC99" />
                    {/* Head */}
                    <ellipse cx="64" cy="50" rx="22" ry="24" fill="#FFCC99" />
                    {/* Hair */}
                    <ellipse cx="64" cy="30" rx="22" ry="12" fill="#78909c" />
                    {/* Eyes */}
                    <ellipse cx="56" cy="50" rx="3" ry="3.5" fill="#455a64" />
                    <ellipse cx="72" cy="50" rx="3" ry="3.5" fill="#455a64" />
                    {/* Smile */}
                    <path d="M57 60 Q64 66 71 60" stroke="#546e7a" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                  </svg>
                )}
              </div>
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
                title="Upload profile photo"
                className="absolute bottom-0 right-0 p-2.5 bg-white rounded-full shadow-lg border border-slate-100 text-primary hover:scale-110 transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {imageUploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              </button>
            </div>
            
            <h2 className="text-2xl font-black text-slate-900 mb-1">{firstName} {lastName}</h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">Medical Provider</span>
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              <span className="text-primary font-black text-[10px] uppercase tracking-widest">Hospital ID: {providerInfo?.data.hospital_id}</span>
            </div>
            
            <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest mx-auto w-fit border border-emerald-100">
              <CheckCircle2 size={14} /> Clinical Verification Active
            </div>
          </div>

          {/* Quick Experience Stat */}
          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-2xl">
             <Medal size={120} className="absolute -right-8 -bottom-8 text-white/5" />
             <div className="relative z-10">
                <p className="text-primary font-black text-[10px] uppercase tracking-[0.2em] mb-4">Experience Milestone</p>
                <div className="flex items-end gap-3 mb-2">
                   <span className="text-4xl font-black">{providerInfo?.data.practicing_since ? new Date().getFullYear() - new Date(providerInfo.data.practicing_since).getFullYear() : '--'}</span>
                   <span className="text-white/60 font-bold mb-1.5 uppercase text-xs tracking-widest">Years of Practice</span>
                </div>
                <p className="text-white/50 text-xs font-medium leading-relaxed italic">Practicing excellence since {providerInfo?.data.practicing_since || 'the beginning'}.</p>
             </div>
          </div>
        </div>

        {/* Right Column: Sections */}
        <div className="lg:col-span-8 space-y-8">
          {/* Professional Credentials Section - Integrated from New API */}
          <section className="bg-white rounded-[2.5rem] p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-2.5 bg-emerald-500/10 rounded-2xl">
                <FileText className="text-emerald-500" size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Professional Credentials</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Medical License Number</label>
                <div className="px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-black text-slate-700">
                  {providerInfo?.data.license_no || 'Pending Verification'}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Practicing Since</label>
                <div className="px-5 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-black text-slate-700 flex items-center gap-2">
                  <Calendar size={14} className="text-primary/60" />
                  {providerInfo?.data.practicing_since || 'N/A'}
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Academic Qualifications</label>
                <div className="flex flex-wrap gap-2 mt-1">
                   {providerInfo?.data.additional_info.qualification.map((q, i) => (
                     <span key={i} className="px-4 py-2 rounded-xl bg-primary/5 text-primary border border-primary/10 text-xs font-black uppercase tracking-widest">
                       {q}
                     </span>
                   )) || <span className="text-slate-400 italic">No qualifications added.</span>}
                </div>
              </div>
            </div>
          </section>

          {/* Clinical Availability Section - Integrated from New API */}
          <section className="bg-white rounded-[2.5rem] p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-amber-500/10 rounded-2xl shrink-0">
                  <Clock className="text-amber-500" size={24} />
                </div>
                <h3 className="text-xl font-black text-slate-900 tracking-tight">Clinical Availability</h3>
              </div>
              {providerInfo?.data.id && (
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setShowAvailability(true)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20 hover:scale-[1.03] active:scale-95 transition-all w-full sm:w-auto shrink-0"
                  >
                    <Calendar size={16} />
                    Add Availability
                  </button>
                  <button
                    onClick={() => setShowUnavailability(true)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-rose-500/20 hover:scale-[1.03] active:scale-95 transition-all w-full sm:w-auto shrink-0"
                  >
                    <AlarmMinus size={16} />
                    Mark Unavailable
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-8">
               {/* Day Tabs */}
               <div className="flex items-center gap-2 overflow-x-auto pb-4 scrollbar-hide">
                  {providerInfo?.data.availabilities.map((day) => (
                    <button
                      key={day.text}
                      onClick={() => setActiveDay(day.text)}
                      className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap border ${
                        activeDay === day.text 
                          ? "bg-slate-900 text-white border-slate-900 shadow-lg" 
                          : "bg-white text-slate-400 border-slate-100 hover:border-primary/20"
                      }`}
                    >
                      {day.text}
                    </button>
                  ))}
               </div>

               {/* Time Slots Visualization */}
               <div className="bg-slate-50 rounded-[2rem] p-5 sm:p-6 border border-slate-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                     <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-xl ${currentDayAvailability?.day ? 'bg-emerald-500' : 'bg-slate-300'} text-white shrink-0`}>
                           <Timer size={16} />
                        </div>
                        <div>
                           <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Schedule Status</p>
                           <p className="text-sm font-black text-slate-800">{currentDayAvailability?.day ? 'Operational' : 'Off-Duty'}</p>
                        </div>
                     </div>
                     <div className="sm:text-right flex items-center gap-3">
                        <button 
                           onClick={handleToggleActiveDay}
                           className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border ${
                              currentDayAvailability?.day 
                              ? 'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100' 
                              : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'
                           }`}
                        >
                           Mark as {currentDayAvailability?.day ? 'Off-Duty' : 'Operational'}
                        </button>
                     </div>
                  </div>

                  {currentDayAvailability?.day && currentDayAvailability.timing.slot.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                       {currentDayAvailability.timing.slot.map((slot, i) => (
                         <div key={i} className="group relative">
                            <div className={`py-2 px-3 rounded-xl border text-[10px] font-bold text-center transition-all ${
                              slot.status === 'available' 
                                ? 'bg-white border-slate-200 text-slate-600 hover:border-emerald-500 hover:text-emerald-600 cursor-default' 
                                : 'bg-slate-100 border-slate-200 text-slate-400 line-through'
                            }`}>
                               {slot.time}
                            </div>
                            {slot.status === 'available' && (
                              <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                         </div>
                       ))}
                    </div>
                  ) : (
                     <div className="py-12 text-center">
                        <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest italic">No slots available for this day.</p>
                     </div>
                  )}



                  {/* {currentDayAvailability?.day && currentDayAvailability.timing.queue.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-slate-200/50">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Queue Consultation Hours</p>
                       <div className="flex flex-wrap gap-4">
                          {currentDayAvailability.timing.queue.map((q, i) => (
                            <div key={i} className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white border border-slate-200">
                               <Clock size={14} className="text-primary" />
                               <span className="text-xs font-black text-slate-700">{q.from_time} - {q.to_time}</span>
                            </div>
                          ))}
                       </div>
                    </div>
                  )} */}

                  {/* Registered Unavailability Blocks */}
                  {unavailabilitiesList && unavailabilitiesList.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-slate-200/80">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                        <AlarmMinus size={14} className="text-rose-500" /> Unavailability & Leave Registry
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {unavailabilitiesList.map((unavail) => {
                          let timeDisplay = "Full Day";
                          if (unavail.timing) {
                            const timing = unavail.timing;
                            if (Array.isArray(timing)) {
                              timeDisplay = timing.join(", ");
                            } else if (typeof timing === "object") {
                              const from = (timing as any).from_time;
                              const to = (timing as any).to_time;
                              if (from && to) {
                                timeDisplay = `${from} - ${to}`;
                              }
                            } else if (typeof timing === "string") {
                              try {
                                const parsed = JSON.parse(timing);
                                if (Array.isArray(parsed)) {
                                  timeDisplay = parsed.join(", ");
                                } else if (parsed && typeof parsed === "object") {
                                  const from = parsed.from_time;
                                  const to = parsed.to_time;
                                  if (from && to) {
                                    timeDisplay = `${from} - ${to}`;
                                  }
                                }
                              } catch (e) {
                                timeDisplay = timing;
                              }
                            }
                          }
                          
                          return (
                            <div
                              key={unavail.id}
                              className="flex items-center justify-between p-4 rounded-2xl bg-white border border-slate-100 shadow-sm"
                            >
                              <div className="flex items-center gap-3">
                                <div className="px-2.5 py-1 bg-rose-50 border border-rose-100 rounded-xl text-[9px] font-black uppercase tracking-wider text-rose-600 shrink-0">
                                  {unavail.available_type || "Leave"}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-black text-slate-800 leading-tight">
                                    {unavail.from_date}
                                  </p>
                                  <p className="text-[10px] font-bold text-slate-400 mt-0.5 truncate">
                                    {timeDisplay}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDeleteUnavailability(unavail.id)}
                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all cursor-pointer shrink-0"
                                title="Delete unavailability block"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
               </div>
            </div>
          </section>

          {/* Personal Info Section */}
          <section className="bg-white rounded-[2.5rem] p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-2.5 bg-primary/10 rounded-2xl">
                <User className="text-primary" size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Personal Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <User size={12} className="text-primary" /> First Name
                </label>
                <InputField value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Enter first name" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <User size={12} className="text-primary" /> Last Name
                </label>
                <InputField value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Enter last name" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Calendar size={12} className="text-primary" /> Date of Birth
                </label>
                <DatePickerInput selectedDate={dob} onDateChange={setDob} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                      <User size={12} className="text-primary" /> Gender
                    </label>
                    <SelectInput selectedValue={gender} onChange={setGender} options={genderOptions} showRequiredIndicator={false} />
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                      <Activity size={12} className="text-rose-400" /> Blood Group
                    </label>
                    <SelectInput selectedValue={blood_group} onChange={setBlood_group} options={bloodGroupOptions} showRequiredIndicator={false} />
                 </div>
              </div>
            </div>
          </section>

          {/* Contact Details Section */}
          <section className="bg-white rounded-[2.5rem] p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-2.5 bg-indigo-500/10 rounded-2xl">
                <Phone className="text-indigo-500" size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Contact Details</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Mail size={12} className="text-indigo-500" /> Email Address
                </label>
                <InputField value={email} onChange={e => setEmail(e.target.value)} placeholder="doctor@hospital.com" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Phone size={12} className="text-indigo-500" /> Phone Number
                </label>
                <PhoneNumberInput 
                  selectedCountry={selectedCountry} 
                  isdCode={isdCode} 
                  setIsdCode={setIsdCode} 
                  mobile={mobile} 
                  setmobile={setmobile} 
                  disabled={false}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Globe size={12} className="text-indigo-500" /> Address Line 1
                </label>
                <InputField value={address1} onChange={e => setAddress1(e.target.value)} placeholder="Hospital / Clinic address" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Globe size={12} className="text-indigo-500" /> Address Line 2
                </label>
                <InputField value={address2} onChange={e => setAddress2(e.target.value)} placeholder="Suite, floor, etc. (Optional)" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Globe size={12} className="text-indigo-500" /> City
                </label>
                <InputField value={city} onChange={e => setCity(e.target.value)} placeholder="City" showRequiredIndicator={false} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 ml-1">
                  <Globe size={12} className="text-indigo-500" /> Zipcode
                </label>
                <InputField value={zipcode} onChange={e => setZipcode(e.target.value)} placeholder="000000" maxLength={6} showRequiredIndicator={false} />
              </div>
            </div>
          </section>

          {/* Regional Settings Section */}
          <section className="bg-white rounded-[2.5rem] p-6 sm:p-8 md:p-10 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-2.5 bg-amber-500/10 rounded-2xl">
                <Globe className="text-amber-500" size={24} />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight">Regional Settings</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country</label>
                <Autocomplete
                  options={countryOptions}
                  getOptionLabel={o => o.label}
                  value={countryOptions.find(o => o.value === selectedCountry?.value) || null}
                  onChange={(_, v) => handleCountryChange(v ? v.value : "")}
                  renderInput={p => <TextField {...p} variant="outlined" fullWidth className="bg-slate-50 rounded-2xl overflow-hidden border-none" sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }} />}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">State / Province</label>
                <Autocomplete
                  options={stateOptions}
                  value={stateOptions.find(o => o.value === state) || null}
                  onChange={(_, v) => setState(v ? v.value : "")}
                  renderInput={p => <TextField {...p} variant="outlined" fullWidth className="bg-slate-50 rounded-2xl overflow-hidden border-none" sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }} />}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Local Time Zone</label>
                <Autocomplete
                  options={timeZoneOptions}
                  getOptionLabel={o => o.label}
                  value={timeZoneOptions.find(o => o.value === selectedTimeZone?.zone_name) || null}
                  onChange={(_, v) => setSelectedTimeZone(v ? { zone_name: v.value, country_code: selectedCountry?.value || "" } : null)}
                  renderInput={p => <TextField {...p} variant="outlined" fullWidth className="bg-slate-50 rounded-2xl overflow-hidden border-none" sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }} />}
                />
              </div>
            </div>
          </section>
        </div>
      </div>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />

      {/* Provider Unavailability Modal */}
      {showUnavailability && providerInfo?.data.id && (
        <ProviderUnavailabilityModal
          providerId={providerInfo.data.id}
          onClose={() => setShowUnavailability(false)}
          onSuccess={async () => {
            setSnackbar({
              open: true,
              message: "Unavailability block saved successfully!",
              severity: "success",
            });
            if (userData?.data?.id) {
              try {
                const [provider, unavailData] = await Promise.all([
                  fetchProviderInfo(userData.data.id),
                  fetchProviderUnavailabilities()
                ]);
                setProviderInfo(provider);
                if (unavailData?.data?.provider_unavailabilities) {
                  setUnavailabilitiesList(unavailData.data.provider_unavailabilities);
                }
              } catch (err) {
                console.error("Failed to refresh provider info:", err);
              }
            }
          }}
        />
      )}
      {/* Provider Availability Modal */}
      {showAvailability && providerInfo?.data && (
        <ProviderAvailabilityModal
          providerData={providerInfo.data}
          onClose={() => setShowAvailability(false)}
          onSuccess={(newProviderData) => {
            setProviderInfo({ ...providerInfo, data: newProviderData });
            setSnackbar({
              open: true,
              message: "Availability generated successfully!",
              severity: "success",
            });
          }}
        />
      )}
      
    </div>
  );
};

export default Accounts;
